import { useEffect, useState, useMemo } from 'react'
import { MapContainer, TileLayer, Marker, Popup, useMap } from 'react-leaflet'
import L from 'leaflet'
import 'leaflet/dist/leaflet.css'
import 'leaflet.fullscreen/dist/Control.FullScreen.css'
import FullScreen from 'leaflet.fullscreen'
import type { CityRequestGroup } from '@/pages/ArtistWaitlistPage'

interface WaitlistMapProps {
  cityGroups: CityRequestGroup[]
}

// Create custom city marker icon with dynamic sizing based on request count
const createCityMarkerIcon = (count: number) => {
  const baseSize = 24
  const scaleMultiplier = 4
  const size = count === 1 ? baseSize : baseSize + Math.log2(count) * scaleMultiplier
  
  const borderWidth = count === 1 ? 2 : Math.min(4, 2 + size / 20)
  const fontSize = count === 1 ? 11 : Math.min(16, 11 + size / 10)
  
  return L.divIcon({
    html: `
      <div class="waitlist-city-marker">
        <div class="waitlist-city-dot" style="width: ${size}px; height: ${size}px; border-width: ${borderWidth}px;">
          <span class="waitlist-city-count" style="font-size: ${fontSize}px;">${count}</span>
        </div>
      </div>
    `,
    className: 'waitlist-city-marker-wrapper',
    iconSize: [size, size],
    iconAnchor: [size / 2, size / 2],
  })
}

// Calculate distance between two points using Haversine formula (returns km)
function calculateDistance(lat1: number, lon1: number, lat2: number, lon2: number): number {
  const earthRadiusKm = 6371
  const dLat = (lat2 - lat1) * Math.PI / 180
  const dLon = (lon2 - lon1) * Math.PI / 180
  const a = 
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) *
    Math.sin(dLon / 2) * Math.sin(dLon / 2)
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a))
  return earthRadiusKm * c
}

// Component to auto-fit bounds
function MapBoundsHandler({ cityGroups }: { cityGroups: CityRequestGroup[] }) {
  const map = useMap()

  useEffect(() => {
    if (cityGroups.length === 0) return

    const bounds: [number, number][] = cityGroups.map((group) => [group.lat, group.lon])

    if (bounds.length > 0) {
      const isMobile = window.innerWidth < 768
      const paddingValue: [number, number] = isMobile ? [30, 30] : [50, 50]
      map.fitBounds(bounds, { padding: paddingValue })
    }
  }, [cityGroups, map])

  return null
}

// Component to add fullscreen control
function FullscreenControl() {
  const map = useMap()

  useEffect(() => {
    // Add fullscreen control to top right position
    const fullscreenControl = new FullScreen({
      position: 'topright',
      forceSeparateButton: true, // Keep separate from zoom controls
    })
    
    map.addControl(fullscreenControl)

    // Cleanup
    return () => {
      map.removeControl(fullscreenControl)
    }
  }, [map])

  return null
}

export function WaitlistMap({ cityGroups }: WaitlistMapProps) {
  const [mapReady, setMapReady] = useState(false)

  // Group cities by geographic proximity (20km radius clustering)
  const cityMarkerGroups = useMemo(() => {
    const CLUSTER_RADIUS_KM = 20
    const clusters: Array<{
      lat: number
      lon: number
      cityGroups: CityRequestGroup[]
      totalCount: number
    }> = []

    cityGroups.forEach(cityGroup => {
      // Find existing cluster within radius
      let foundCluster = false
      for (const cluster of clusters) {
        const distance = calculateDistance(
          cityGroup.lat, 
          cityGroup.lon, 
          cluster.lat, 
          cluster.lon
        )
        
        if (distance <= CLUSTER_RADIUS_KM) {
          cluster.cityGroups.push(cityGroup)
          cluster.totalCount += cityGroup.count
          // Recalculate centroid
          const totalLat = cluster.cityGroups.reduce((sum, g) => sum + g.lat, 0)
          const totalLon = cluster.cityGroups.reduce((sum, g) => sum + g.lon, 0)
          cluster.lat = totalLat / cluster.cityGroups.length
          cluster.lon = totalLon / cluster.cityGroups.length
          foundCluster = true
          break
        }
      }
      
      // If no cluster found within radius, create new cluster
      if (!foundCluster) {
        clusters.push({
          lat: cityGroup.lat,
          lon: cityGroup.lon,
          cityGroups: [cityGroup],
          totalCount: cityGroup.count,
        })
      }
    })

    return clusters
  }, [cityGroups])

  const centerCoordinates: [number, number] = [20, 0] // World center

  return (
    <>
      <style>{`
        /* Waitlist city marker styles */
        .waitlist-city-marker-wrapper {
          background: none !important;
          border: none !important;
        }
        
        .waitlist-city-marker {
          position: relative;
          cursor: pointer;
        }
        
        .waitlist-city-dot {
          background: #ef4444;
          border: solid white;
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          box-shadow: 0 2px 8px rgba(0,0,0,0.2);
          transition: transform 0.2s;
        }
        
        .waitlist-city-dot:hover {
          transform: scale(1.1);
        }
        
        .waitlist-city-count {
          color: white;
          font-weight: 700;
        }
        
        /* Custom popup styles */
        .leaflet-popup-content-wrapper {
          border-radius: 8px;
          padding: 0;
        }
        
        .leaflet-popup-content {
          margin: 12px;
          min-width: 200px;
        }
        
        .popup-city-header {
          font-weight: 700;
          font-size: 16px;
          margin-bottom: 8px;
          border-bottom: 2px solid #e5e7eb;
          padding-bottom: 4px;
        }

        .popup-city-list {
          max-height: 200px;
          overflow-y: auto;
        }

        .popup-city-item {
          padding: 6px 0;
          border-bottom: 1px solid #f3f4f6;
        }

        .popup-city-item:last-child {
          border-bottom: none;
        }

        .popup-city-name {
          font-weight: 600;
          font-size: 14px;
          margin-bottom: 2px;
        }

        .popup-request-count {
          font-size: 12px;
          color: #6b7280;
        }

        .popup-flash-custom-split {
          font-size: 11px;
          color: #9ca3af;
          margin-top: 2px;
        }
      `}</style>
      
      <div className="w-full h-full border border-border overflow-hidden md:rounded-lg">
        <MapContainer
          center={centerCoordinates}
          zoom={2}
          style={{ height: '100%', width: '100%' }}
          className="z-10"
          zoomControl={window.innerWidth >= 768}
          whenReady={() => setMapReady(true)}
        >
          <TileLayer
            attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
            url="https://{s}.basemaps.cartocdn.com/light_all/{z}/{x}/{y}{r}.png"
          />
          
          {/* Auto-adjust bounds based on city groups */}
          {mapReady && <MapBoundsHandler cityGroups={cityGroups} />}
          
          {/* Add fullscreen control */}
          <FullscreenControl />
          
          {/* City markers */}
          {cityMarkerGroups.map((cluster, index) => (
            <Marker
              key={`cluster-${index}`}
              position={[cluster.lat, cluster.lon]}
              icon={createCityMarkerIcon(cluster.totalCount)}
            >
              <Popup maxWidth={300}>
                {cluster.cityGroups.length === 1 ? (
                  // Single city
                  <>
                    <div className="popup-city-header">
                      {cluster.cityGroups[0].city}, {cluster.cityGroups[0].country}
                    </div>
                    <div className="popup-request-count">
                      {cluster.totalCount} {cluster.totalCount === 1 ? 'request' : 'requests'}
                    </div>
                    <div className="popup-flash-custom-split">
                      {cluster.cityGroups[0].requests.filter(r => r.isFlash).length} flash · {' '}
                      {cluster.cityGroups[0].requests.filter(r => !r.isFlash).length} custom
                    </div>
                  </>
                ) : (
                  // Multiple cities clustered together
                  <>
                    <div className="popup-city-header">
                      {cluster.totalCount} requests from {cluster.cityGroups.length} cities
                    </div>
                    <div className="popup-city-list">
                      {cluster.cityGroups.map((cityGroup, idx) => (
                        <div key={idx} className="popup-city-item">
                          <div className="popup-city-name">
                            {cityGroup.city}, {cityGroup.country}
                          </div>
                          <div className="popup-request-count">
                            {cityGroup.count} {cityGroup.count === 1 ? 'request' : 'requests'}
                          </div>
                          <div className="popup-flash-custom-split">
                            {cityGroup.requests.filter(r => r.isFlash).length} flash · {' '}
                            {cityGroup.requests.filter(r => !r.isFlash).length} custom
                          </div>
                        </div>
                      ))}
                    </div>
                  </>
                )}
              </Popup>
            </Marker>
          ))}
        </MapContainer>
        
        {cityGroups.length === 0 && (
          <div className="absolute inset-0 flex items-center justify-center bg-background/80 z-20">
            <div className="text-center">
              <p className="text-muted-foreground">
                No requests in this region
              </p>
            </div>
          </div>
        )}
      </div>
    </>
  )
}

